import React from 'react';

/**
 * The TayLord Arms branded estimate layout. Matches the printed paper form 1:1.
 * Navy (#15315c) + orange (#f57c00) palette, oval logo, section bars, boxed totals.
 */

const NAVY = '#15315c';
const ORANGE = '#f57c00';

const usd = (n) => `$${(Number(n) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    if (typeof iso === 'string' && iso.length === 10) {
      return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    }
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return iso; }
};

const FieldLine = ({ label, value, testid, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <div className="flex items-baseline gap-2">
      <span className="uppercase font-bold text-[10px] tracking-wider shrink-0" style={{ color: NAVY }}>{label}</span>
      <div className="flex-1 border-b border-slate-700 min-h-[18px] text-sm text-slate-900 font-medium px-1 break-words" data-testid={testid}>{value || '\u00A0'}</div>
    </div>
  </div>
);

const SectionBar = ({ children }) => (
  <h3 className="font-extrabold text-sm tracking-wider mb-2 pb-1 border-b-2" style={{ color: NAVY, borderColor: NAVY }}>{children}</h3>
);

const ItemsTable = ({ title, rows, columns, testid }) => (
  <div className="border-2 rounded-sm overflow-hidden" style={{ borderColor: NAVY }} data-testid={testid}>
    <div className="text-center font-extrabold text-white text-sm tracking-widest py-1.5" style={{ background: NAVY }}>{title}</div>
    <table className="w-full text-[11px]">
      <thead className="text-center font-bold" style={{ color: NAVY }}>
        <tr className="border-b" style={{ borderColor: NAVY }}>
          {columns.map((c) => <th key={c.k} className="py-1 px-1" style={{ width: c.w }}>{c.h}</th>)}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: Math.max(rows.length, 6) }).map((_, idx) => {
          const r = rows[idx];
          return (
            <tr key={`${testid}-${idx}`} className="border-b border-slate-300">
              {columns.map((c) => (
                <td key={c.k} className="px-1 py-1 align-bottom border-b border-slate-300 tabular-nums" style={{ textAlign: c.align || 'left' }}>
                  <span className="block min-h-[14px]">{r ? (c.fmt ? c.fmt(r) : r[c.k]) : '\u00A0'}</span>
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const StructuredEstimateView = ({ estimate }) => {
  const items = estimate.items_computed || estimate.line_items || [];
  const labor = items.filter((i) => (i.category || 'parts') === 'labor');
  const parts = items.filter((i) => (i.category || 'parts') !== 'labor');
  const firearm = estimate.firearm || {};
  return (
    <div data-testid="estimate-structured-render" className="bg-white text-slate-900 p-6 sm:p-8 max-w-4xl mx-auto shadow-lg print:shadow-none print:p-6 rounded-md print:rounded-none">
      {/* ===== Header ===== */}
      <header className="grid grid-cols-12 gap-4 items-start mb-4 pb-4 border-b-2" style={{ borderColor: NAVY }}>
        <div className="col-span-3">
          <img src="/logo-taylordarms-oval.png" alt="TayLord Arms" className="w-full max-w-[170px] object-contain" />
        </div>
        <div className="col-span-6 text-center pt-1">
          <h1 className="font-extrabold text-5xl tracking-tight" style={{ color: NAVY }}>ESTIMATE</h1>
          <p className="font-bold text-sm tracking-[0.3em] mt-1" style={{ color: ORANGE }}>— PROFESSIONAL FIREARM SERVICES —</p>
          <div className="mt-3 space-y-0.5 text-[12px]" style={{ color: NAVY }}>
            <p>1102 Maplecrest Dr</p>
            <p>Moncks Corner, SC 29461</p>
            <p>www.taylordarms.com</p>
            <p>info.taylordarms@gmail.com</p>
            <p>843.209.4304</p>
          </div>
        </div>
        <div className="col-span-3 border-2 rounded-sm p-3 text-[11px] space-y-2" style={{ borderColor: NAVY, color: NAVY }}>
          <div>
            <span className="font-bold tracking-wider">ESTIMATE # </span>
            <span className="border-b border-slate-700 inline-block min-w-[80px] font-mono text-sm font-bold">{estimate.estimate_number || '\u00A0'}</span>
          </div>
          <div>
            <span className="font-bold tracking-wider">DATE </span>
            <span className="border-b border-slate-700 inline-block min-w-[120px]">{fmtDate(estimate.created_at)}</span>
          </div>
          <div>
            <span className="font-bold tracking-wider">EXPIRATION DATE </span>
            <span className="border-b border-slate-700 inline-block min-w-[100px]">{fmtDate(estimate.valid_until)}</span>
          </div>
          <p className="font-bold italic text-[10px] text-center pt-1" style={{ color: ORANGE }}>ESTIMATE VALID FOR 14 DAYS</p>
        </div>
      </header>

      {/* ===== Customer Information ===== */}
      <section className="mb-4">
        <SectionBar>CUSTOMER INFORMATION</SectionBar>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2">
          <FieldLine label="NAME" value={estimate.customer_name} testid="render-customer-name" />
          <FieldLine label="PHONE" value={estimate.customer_phone} />
          <FieldLine label="EMAIL" value={estimate.customer_email} />
          <FieldLine label="ADDRESS" value={estimate.customer_address} />
        </div>
      </section>

      {/* ===== Firearm Information ===== */}
      <section className="mb-4">
        <SectionBar>FIREARM INFORMATION</SectionBar>
        <div className="grid grid-cols-3 gap-x-8 gap-y-2">
          <FieldLine label="MAKE" value={firearm.make} testid="render-firearm-make" />
          <FieldLine label="MODEL" value={firearm.model} />
          <FieldLine label="CALIBER" value={firearm.caliber} />
          <FieldLine label="TYPE" value={firearm.type} />
          <FieldLine label="SERIAL #" value={firearm.serial} />
          <FieldLine label="COLOR / FINISH" value={firearm.color} />
        </div>
        <div className="grid grid-cols-1 mt-2">
          <FieldLine label="OTHER / OPTIC / ACCESSORIES" value={firearm.accessories} />
        </div>
      </section>

      {/* ===== Service Requested ===== */}
      <section className="mb-4">
        <SectionBar>SERVICE REQUESTED / DESCRIPTION OF WORK</SectionBar>
        <div className="text-sm text-slate-900 whitespace-pre-wrap min-h-[3em] border-b border-slate-700 pb-1" data-testid="render-service-description">
          {estimate.service_description || '\u00A0'}
        </div>
      </section>

      {/* ===== Labor + Parts tables ===== */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <ItemsTable
          title="LABOR"
          testid="render-labor-table"
          rows={labor}
          columns={[
            { k: 'label', h: 'DESCRIPTION', w: '70%', align: 'left' },
            { k: 'line_total', h: 'ESTIMATED', w: '30%', align: 'right', fmt: (r) => `$ ${(r.line_total || (r.qty * r.unit_price) || 0).toFixed(2)}` },
          ]}
        />
        <ItemsTable
          title="PARTS / MATERIALS"
          testid="render-parts-table"
          rows={parts}
          columns={[
            { k: 'label', h: 'DESCRIPTION', w: '45%', align: 'left' },
            { k: 'qty', h: 'QTY', w: '12%', align: 'center' },
            { k: 'unit_price', h: 'UNIT PRICE', w: '20%', align: 'right', fmt: (r) => `$ ${(r.unit_price || 0).toFixed(2)}` },
            { k: 'line_total', h: 'TOTAL', w: '23%', align: 'right', fmt: (r) => `$ ${(r.line_total || (r.qty * r.unit_price) || 0).toFixed(2)}` },
          ]}
        />
      </div>

      {/* ===== Completion / Notes (left) + Totals (right) ===== */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <section>
          <div className="mb-3">
            <FieldLine label="ESTIMATED COMPLETION DATE" value={fmtDate(estimate.estimated_completion_date)} />
          </div>
          <div>
            <span className="uppercase font-bold text-[10px] tracking-wider block mb-1" style={{ color: NAVY }}>GUNSMITH NOTES</span>
            <div className="text-sm text-slate-900 whitespace-pre-wrap min-h-[3em] border-b border-slate-700">
              {estimate.gunsmith_notes || '\u00A0'}
            </div>
          </div>
        </section>
        <section className="border-2 rounded-sm overflow-hidden" style={{ borderColor: NAVY }}>
          <table className="w-full text-sm">
            <tbody>
              <tr><td className="font-bold py-1.5 px-3" style={{ color: NAVY }}>LABOR TOTAL</td><td className="text-right py-1.5 px-3 tabular-nums">$ {(estimate.labor_total || 0).toFixed(2)}</td></tr>
              <tr><td className="font-bold py-1.5 px-3 border-t border-slate-300" style={{ color: NAVY }}>PARTS TOTAL</td><td className="text-right py-1.5 px-3 border-t border-slate-300 tabular-nums">$ {(estimate.parts_total || 0).toFixed(2)}</td></tr>
              <tr><td className="font-bold py-1.5 px-3 border-t border-slate-300" style={{ color: NAVY }}>TAX ({estimate.tax_rate || 0}%)</td><td className="text-right py-1.5 px-3 border-t border-slate-300 tabular-nums">$ {(estimate.tax_amount || 0).toFixed(2)}</td></tr>
              <tr data-testid="render-total"><td className="font-extrabold text-white py-2 px-3" style={{ background: NAVY }}>ESTIMATED TOTAL</td><td className="text-right py-2 px-3 font-extrabold text-white tabular-nums" style={{ background: NAVY }}>$ {(estimate.total || 0).toFixed(2)}</td></tr>
            </tbody>
          </table>
        </section>
      </div>

      {/* ===== Deposit / Payment ===== */}
      <section className="border-2 rounded-sm p-3 mb-4" style={{ borderColor: NAVY }}>
        <h3 className="font-extrabold text-sm tracking-wider mb-2" style={{ color: NAVY }}>DEPOSIT / PAYMENT</h3>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <div className="flex items-baseline gap-2">
            <span className="uppercase font-bold text-[10px] tracking-wider" style={{ color: NAVY }}>DEPOSIT REQUIRED:</span>
            <span className="border-b border-slate-700 flex-1 px-1 tabular-nums">$ {(estimate.deposit_required || 0).toFixed(2)}</span>
          </div>
          <div />
          <div className="flex items-baseline gap-2">
            <span className="uppercase font-bold text-[10px] tracking-wider" style={{ color: NAVY }}>DEPOSIT PAID:</span>
            <span className="border-b border-slate-700 flex-1 px-1 tabular-nums" data-testid="render-deposit-paid">$ {(estimate.deposit_paid || 0).toFixed(2)}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="uppercase font-bold text-[10px] tracking-wider" style={{ color: NAVY }}>DATE:</span>
            <span className="border-b border-slate-700 flex-1 px-1">{fmtDate(estimate.deposit_paid_date)}</span>
          </div>
          <div className="col-span-2 flex items-center gap-2 mt-1">
            <span className={`inline-block w-4 h-4 border-2 ${estimate.full_payment_due_on_completion ? 'bg-slate-900' : 'bg-white'}`} style={{ borderColor: NAVY }}>
              {estimate.full_payment_due_on_completion && <span className="text-white text-xs leading-none flex items-center justify-center">✓</span>}
            </span>
            <span className="uppercase font-bold text-[11px] tracking-wider" style={{ color: NAVY }}>FULL PAYMENT DUE UPON COMPLETION</span>
          </div>
        </div>
      </section>

      {/* ===== Terms & Conditions ===== */}
      <section className="border-2 rounded-sm p-3 mb-4 text-[11px] leading-relaxed" style={{ borderColor: NAVY, color: NAVY }}>
        <h3 className="font-extrabold text-sm tracking-wider mb-2">TERMS &amp; CONDITIONS</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>This estimate is valid for 14 days from the date above.</li>
          <li>Additional charges may apply if additional work or parts are required.</li>
          <li>Firearm(s) not picked up within 30 days of completion may be subject to storage fees.</li>
          <li>TayLord Arms is not responsible for loss, theft, or damage to firearms or accessories due to causes <em>beyond our control</em>.</li>
          <li>By signing below, you acknowledge that you are the legal owner of the firearm(s) listed above and authorize TayLord Arms to perform the work described. Payment is due in full upon completion of services unless otherwise agreed.</li>
        </ul>
        <div className="grid grid-cols-12 gap-3 mt-4 items-end">
          <div className="col-span-6 flex items-end gap-1">
            <span className="font-bold">X</span>
            <div className="flex-1 border-b border-slate-700 h-6" />
          </div>
          <div className="col-span-4 border-b border-slate-700 h-6" />
          <div className="col-span-2 border-b border-slate-700 h-6" />
          <div className="col-span-6 text-[10px] font-bold tracking-wider">CUSTOMER SIGNATURE</div>
          <div className="col-span-4 text-[10px] font-bold tracking-wider">TAYLORD ARMS REPRESENTATIVE</div>
          <div className="col-span-2 text-[10px] font-bold tracking-wider">DATE</div>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer className="text-center mt-4 pt-2 border-t-2" style={{ borderColor: NAVY }}>
        <p className="font-extrabold tracking-wider text-sm" style={{ color: NAVY }}>THANK YOU FOR CHOOSING TAYLORD ARMS.</p>
        <p className="font-bold italic text-xs mt-1" style={{ color: ORANGE }}>KEEP YOUR FIREARM RUNNING RIGHT.</p>
      </footer>
    </div>
  );
};

export default StructuredEstimateView;
